<template>
  <v-app>
    <v-navigation-drawer
      v-model="nav"
      app
      stateless
      right
      width="300"
    >
      <div class="ma-6">
        <a href="/">
          <v-img
            :src="require('./assets/logo.png')"
          ></v-img>
        </a>
        <h1 class="title text-center my-2">Site Matching</h1>
        <v-alert type="info" color="primary" tile dense>Click or tap on the map to set a location</v-alert>
        <h2 class="subtitle-1 font-weight-medium mt-4">Environmental variables</h2>
        <v-checkbox v-model="envVar" class="ml-2" label="Mean Ann. Temp." value="MAT"></v-checkbox>
        <v-checkbox v-model="envVar" class="ml-2" label="Mean Ann. Precip." value="MAP"></v-checkbox>
        <v-checkbox v-model="envVar" class="ml-2" label="Temp. Seasonality" value="TS"></v-checkbox>
        <v-checkbox v-model="envVar" class="ml-2" label="Precip. Seasonality" value="PS"></v-checkbox>
        <v-checkbox v-model="envVar" class="ml-2" label="Aspect" value="Aspect"></v-checkbox>
        <v-checkbox v-model="envVar" class="ml-2" label="Topo. Wetness Index" value="TWI"></v-checkbox>
        <h2 class="subtitle-1 font-weight-medium mt-4">Future Climate Scenario</h2>
        <h3 class="subtitle-2">Severity</h3>
        <v-radio-group v-model="rcp" class="ml-2">
          <v-radio label="Moderate (rcp4.5)" value="rcp45"></v-radio>
          <v-radio label="Severe (rcp8.5)" value="rcp85"></v-radio>
        </v-radio-group>
        <h3 class="subtitle-2">Time</h3>
        <v-radio-group v-model="epoch" class="ml-2">
          <v-radio label="2050" value="2050"></v-radio>
          <v-radio label="2070" value="2070"></v-radio>
        </v-radio-group>
        <h2 class="subtitle-1 font-weight-medium mt-4" v-show="showingResult">Map Layers</h2>
        <v-checkbox v-model="legendLayers" class="ml-2" label="Current climate match" value="current" disabled v-show="showingResult"></v-checkbox>
        <v-checkbox v-model="legendLayers" class="ml-2" label="Future climate match" value="future" v-show="showingResult"></v-checkbox>
        <v-btn
          class="pa-2 my-4"
          large
          block
          outlined
          :disabled="!(showingResult || inFlightRequest_current || inFlightRequest_future)"
          @click="clear"
          >
          <v-icon left>mdi-close</v-icon>
          Clear
        </v-btn>
        <v-btn
          class="pa-2 mt-4 mb-0 generateReportBtn"
          large
          block
          outlined
          @click.stop="downloadResults()"
          :disabled="!showingResult"
          :loading="!!inFlightRequest_downloadResults"
          >
          <v-icon left>mdi-file-document</v-icon>
          Generate Report
          <span slot="loader"><v-icon left class="loading-spin">mdi-cached</v-icon>Generating report...</span>
        </v-btn>
        <v-progress-linear
          v-show="inFlightRequest_downloadResults"
          class="mt-0"
          height="12"
          :value="downloadResultsProgress"
          ></v-progress-linear>
        <v-btn class="pa-2 my-4"
          color="primary"
          large
          block
          href="/"
          >
          <v-icon left>mdi-home</v-icon>
          Return to main site
        </v-btn>
      </div>
    </v-navigation-drawer>
    <v-content>
      <div class="map-notifications" v-show="inFlightRequest_current || inFlightRequest_future">
        <v-alert color="white" class="loading-alert" max-width="200">
          <v-progress-circular indeterminate color="primary" class="mr-2"></v-progress-circular>
          <span>Loading result...</span>
        </v-alert>
      </div>
      <div class="legend" v-show="showingResult">
        <i class="current"></i><span>Current climate match</span><br>
        <i class="future"></i><span>Future climate match</span><br>
      </div>
      <div id="map"></div>
    </v-content>
    <v-snackbar
      v-model="apiRequestError"
      bottom
      color="error"
      :timeout="0"
    >
      <span v-if="apiRequestErrorMessage">{{apiRequestErrorMessage}}</span>
      <span v-else>Unable to obtain results</span>
      <v-btn
        dark
        text
        @click="apiRequestError = false; apiRequestErrorMessage = null"
      >
        Close
      </v-btn>
    </v-snackbar>
    <v-snackbar
      v-model="reportRequestError"
      bottom
      color="error"
      :timeout="0"
    >
      Unable to generate report
      <v-btn
        dark
        text
        @click="reportRequestError = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-app>
</template>

<script>
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/lib/mapbox-gl-geocoder.css'

mapboxgl.accessToken = 'pk.eyJ1IjoicmVzdG9yZWFuZHJlbmV3IiwiYSI6ImNqYzlucmtxYjAwbnkyd3F3YXUyMGxsdjMifQ.17jNpwzb5KRvTp7AO4v9Qg'

import { request } from 'd3-request'
import queryString from 'query-string'
import bbox from '@turf/bbox'

const layerColor = {
  current: '#8eaffe',
  future: '#edb87d'
}

const emptyGeoJSON = {
  type: 'FeatureCollection',
  features: []
}

export default {
  name: 'App',

  data: () => ({
    nav: true,

    showingResult: false,

    marker: null,
    geocoder: null,

    lng: null,
    lat: null,

    scenarios: ['current', 'future'],

    envVar: ['MAT', 'MAP'],
    rcp: 'rcp45',
    epoch: '2050',

    legendLayers: ['current', 'future'],

    inFlightRequest_current: null,
    inFlightRequest_future: null,

    inFlightRequest_downloadResults: null,

    downloadResultsProgress: 0,
    downloadResultsInterval: null,

    apiRequestError: false,
    reportRequestError: false,

    apiRequestErrorMessage: null,

    bbox: {
      current: new mapboxgl.LngLatBounds(),
      future: new mapboxgl.LngLatBounds()
    },

    // store the geojson so it can be passed to print service to speed up PDF
    geojson: {}
  }),

  computed: {
  },

  watch: {
    rcp: function () {
      this.apiRequest({
        layers: ['future'] // only update future layer
      })
    },
    epoch: function () {
      this.apiRequest({
        layers: ['future'] // only update future layer
      })
    },
    envVar: function () {
      this.apiRequest()
    },
    legendLayers: function (visibleLayers) {
      this.scenarios.forEach(scenario => {
        this.map.setLayoutProperty(`${scenario}`, 'visibility', visibleLayers.includes(scenario) ? 'visible' : 'none')
        this.map.setLayoutProperty(`${scenario}-outline`, 'visibility', visibleLayers.includes(scenario) ? 'visible' : 'none')
      })
    }
  },

  mounted () {
    this.map = new mapboxgl.Map({
      container: 'map',
      style: 'mapbox:///mapbox/streets-v11',
      center: [147.16, -32.94],
      zoom: 4,
      bounds: [139.965,-38.030,155.258,-27.839], // fit to NSW
      fitBoundsOptions: {
        padding: 100
      },
      dragRotate: false
    })

    this.map.addControl(new mapboxgl.NavigationControl({
      showCompass: false
    }))

    this.map.addControl(new mapboxgl.ScaleControl())

    this.geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: true
    })
    this.map.addControl(this.geolocate)

    this.geocoder = new MapboxGeocoder({
      mapboxgl: mapboxgl,
      accessToken: mapboxgl.accessToken,
      marker: false,
      countries: 'au',
      placeholder: 'Find a place'
    })
    this.geocoder.on('result', e => {
      this.lng = e.result.center[0]
      this.lat = e.result.center[1]

      this.setMarker()
    })
    this.map.addControl(this.geocoder, 'top-left')

    this.map.on('load', () => {
      this.map.resize()

      this.scenarios.forEach(layer => {
        this.map.addSource(layer, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: []
          }
        })

        this.map.addLayer({
          id: layer,
          type: 'fill',
          source: layer,
          paint: {
            'fill-color': layerColor[layer],
            'fill-opacity': 0.5
          }
        })
        this.map.addLayer({
          id: `${layer}-outline`,
          type: 'line',
          source: layer,
          paint: {
            'line-color': layerColor[layer],
            'line-width': 2
          }
        })
      })

      this.map.on('click', e => {
        this.lng = e.lngLat.lng
        this.lat = e.lngLat.lat

        this.setMarker()
      })
    })
  },

  methods: {
    downloadResults: function () {
      this.downloadResultsProgress = 0

      if (this.downloadResultsInterval) {
        window.clearInterval(this.downloadResultsInterval)
        this.downloadResultsInterval = null
      }

      // tick every 50ms to reach 100% in 12
      const totalEstimatedTime = 12 * 1000 // 12 as milliseconds
      const intervalDuration = 50 // in milliseconds
      const totalTicks = totalEstimatedTime / intervalDuration

      this.downloadResultsInterval = window.setInterval(() => {
        this.downloadResultsProgress += 100 / totalTicks
      }, intervalDuration)

      this.inFlightRequest_downloadResults = request('/sitematch/print')
        .mimeType('application/pdf')
        .header('Content-Type', 'application/json; charset=UTF-8')
        .responseType('blob')
        .post(JSON.stringify({

          lng: this.lng.toFixed(4),
          lat: this.lat.toFixed(4),
          envVars: this.envVar.join(','),
          rcp: this.rcp,
          epoch: this.epoch,

          current_climate_match: this.geojson.current,
          future_climate_match: this.geojson.future

        }), (err, res) => {
          if (err) {
            this.reportRequestError = true
            console.error(err);
          } else {
            const fileName = `Restore and Renew Site Matching Results.pdf`;
            if (navigator.msSaveBlob) {
              navigator.msSaveBlob(res.response, fileName);
            } else {
              const a = document.createElement("a");
              a.href = window.URL.createObjectURL(res.response);
              a.download = fileName;
              document.body.appendChild(a);
              a.click();
              a.remove();
            }
          }
          this.inFlightRequest_downloadResults = null;

          if (this.downloadResultsInterval) {
            window.clearInterval(this.downloadResultsInterval)
            this.downloadResultsInterval = null
          }
        })

    },
    clear: function () {
      this.scenarios.forEach(layer => {
        this.bbox[layer] = new mapboxgl.LngLatBounds();

        this.map.getSource(layer).setData(emptyGeoJSON)
        delete this.geojson.layer

        if (this[`inFlightRequest_${layer}`]) {
          this[`inFlightRequest_${layer}`].abort()
          this[`inFlightRequest_${layer}`] = null
        }
      })
      this.showingResult = false

      if (this.marker) {
        this.marker.remove()
        this.marker = null
      }

      if (this.geocoder) {
        this.geocoder.clear()
      }
    },
    setMarker: function () {
      if (this.marker) {
        this.marker.setLngLat([this.lng, this.lat])
      } else {
        // wierd rendering issues shows up if chain setLngLat and addTo onto the end of new mapboxgl.Marker()
        // workaround this by avoiding chaining
        // https://twitter.com/alantgeo/status/1267024845037948933
        this.marker = new mapboxgl.Marker({
          draggable: true,
          color: this.$vuetify.theme.themes.light.primary
        })
        this.marker.setLngLat([this.lng, this.lat])
        this.marker.addTo(this.map)

        // prevent clicking the marker or a small drag triggering a map click
        this.marker.getElement().addEventListener('click', e => {
           e.stopPropagation()
        })

        this.marker.on('dragend', (e) => {
          const lngLat = e.target.getLngLat()

          this.lng = lngLat.lng
          this.lat = lngLat.lat
          this.apiRequest()
        })
      }

      this.apiRequest()
    },
    apiRequest: function(options) {
      if (this.lng !== null && this.lat !== null) {
        this.scenarios.forEach(layer => {
          if (options && options.layers && !options.layers.includes(layer)) {
            // if a layers options was set, and this layer isn't in the layers then skip it
            return
          }

          if (this[`inFlightRequest_${layer}`]) {
            this[`inFlightRequest_${layer}`].abort()
            this[`inFlightRequest_${layer}`] = null
          }

          // clear out any pre-existing data on the map while a request is in progress
          this.map.getSource(layer).setData(emptyGeoJSON)
          delete this.geojson.layer

          const params = {
            longitude: this.lng,
            latitude: this.lat,
            envVars: 'c(' + this.envVar.map(v => `"${v}"`).join(',') + ')'
          }
          if (layer === 'future') {
            params.rcp = `"${this.rcp}"`
            params.epoch = `"${this.epoch}"`
          }

          this[`inFlightRequest_${layer}`] = request(`/api/${layer}-site-match`)
            .mimeType('application/json')
            .header('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8')
            .post(queryString.stringify(params), (err, xhr) => {
              this[`inFlightRequest_${layer}`] = null

              if (err) {
                this.apiRequestError = true
                if (err.target && err.target.status === 503) {
                  this.apiRequestErrorMessage = 'Service too busy, try again in a few seconds'
                }
                console.log(err)
              } else {
                const geojson = JSON.parse(xhr.responseText)
                this.showingResult = true
                this.map.getSource(layer).setData(geojson)
                this.geojson[layer] = geojson

                this.bbox[layer] = bbox(geojson)

                // find and fit to bounds of both layers
                let bounds = new mapboxgl.LngLatBounds();
                Object.values(this.bbox).forEach(bound => { bounds = bounds.extend(bound) })
                this.map.fitBounds(bounds, {
                  padding: 30
                })
              }
            })
        })
      }
    }
  }
};
</script>
<style>
  html {
    overflow-y: auto !important;
  }
  #map {
    width: 100%;
    height: 100%;
  }
  .legend {
    position: absolute;
    margin: 10px;
    padding: 8px;
    border-radius: 5px;
    box-shadow: 0px 0px 5px 0px rgba(0,0,0,0.5);
    bottom: 20px;
    right: 0;
    z-index: 1;
    background-color: white;
  }
  .legend i {
    width: 18px;
    height: 18px;
    float: left;
    margin: 2px 8px 0 0;
  }
  .legend span {
    position: relative;
  }
  .legend i.current {
    background-color: #8eaffe;
  }
  .legend i.future {
    background-color: #edb87d;
  }

  .map-notifications {
    position: absolute;
    width: 100%;
    margin-top: 10px;
    text-align: center;
    z-index: 10;
  }
  .loading-alert {
    margin: auto !important;
    box-shadow: 0px 0px 5px 0px rgba(0,0,0,0.5);
  }

  .v-input--checkbox .v-messages {
    display: none;
  }
  .v-input--radio-group .v-messages {
    display: none;
  }
  .v-input--checkbox .v-input__slot {
    margin-bottom: 0;
  }
  .v-input--checkbox {
    margin-top: 0 !important;
    padding-top: 4px !important;
  }
  .v-input--selection-controls {
    margin-top: 0 !important;
  }

  .loading-spin {
    animation: loader 1s infinite;
    display: flex;
  }
  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes loader {
    from {
      transform: rotate(0);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .generateReportBtn .v-btn__content {
    flex-wrap: wrap;
  }
</style>
